import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasFinancialAccess } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import CoordinatorAssign from "../components/CoordinatorAssign";
import FileUpload from "../components/FileUpload";
import ContentDetailModal from "../components/ContentDetailModal";
import ServiceWorkspacePanel from "../components/ServiceWorkspacePanel";
import BookingTimelineStrip from "../components/BookingTimelineStrip";
import { SW_SCHEMAS, SW_KIND_BY_DIVISION } from "../config/serviceWorkspaceSchemas";
import {
  ChevronLeft, MapPin, Calendar as CalIcon, Users as UsersIcon,
  Wallet, Plus, ArrowRight, Clock, User, Pencil, Package, Lock, Trash2, FileText, UserPlus,
} from "lucide-react";
import { formatDate, formatDateLong, formatIDR, formatIDRShort, clientName, daysUntil, formatApiError } from "../lib/format";
import {
  BOOKING_STATUSES, BOOKING_STATUS_LABEL, SERVICE_STATUSES, SERVICE_STATUS_LABEL,
  TASK_PRIORITIES, TASK_STATUSES, TASK_PRIORITY_LABEL, TASK_STATUS_LABEL,
  PAYMENT_TYPES, divisionById,
} from "../lib/constants";

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const financial = hasFinancialAccess(user);
  const isManagement = user?.role === "owner" || user?.role === "admin";
  const canOrder = ["owner", "admin", "general_manager", "division_manager", "division_admin"].includes(user?.role);
  const [b, setB] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [finance, setFinance] = useState(null);
  const [activities, setActivities] = useState([]);
  const [lines, setLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [linkedContent, setLinkedContent] = useState([]);
  const [openContentId, setOpenContentId] = useState(null);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [editService, setEditService] = useState(null);
  const [deleteServiceState, setDeleteService] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [noteText, setNoteText] = useState("");

  // ---- Grouped-tab navigation (Ringkasan / Pelaksanaan / Keuangan / File & Aktivitas) ----
  const [searchParams, setSearchParams] = useSearchParams();
  const normalizeIncoming = (t) => (t === "documents" ? "team" : (t || "overview")); // documents → team alias
  const [sub, setSub] = useState(normalizeIncoming(searchParams.get("tab")));
  const GROUPS = useMemo(() => ([
    { key: "ringkasan",       label: "Ringkasan",         subs: ["overview", "services"] },
    { key: "pelaksanaan",     label: "Pelaksanaan",       subs: ["orders", "tasks", "ops", "content"] },
    { key: "keuangan",        label: "Keuangan",          subs: financial ? ["payments", "finance"] : [] },
    { key: "file_aktivitas",  label: "File & Aktivitas",  subs: ["team", "notes"] },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [financial]);
  const SUB_LABELS = {
    overview: "Ringkasan Umum", services: "Layanan Divisi",
    orders: "Pemesanan", tasks: "Tugas", ops: "Ops Divisi", content: "Konten",
    payments: "Pembayaran", finance: "Ringkasan Keuangan",
    team: "Tim & File", notes: "Catatan / Aktivitas",
  };
  const activeGroup = useMemo(() => {
    const g = GROUPS.find((gr) => gr.subs.includes(sub));
    return g?.key || "ringkasan";
  }, [sub, GROUPS]);
  const activeSubs = GROUPS.find((g) => g.key === activeGroup)?.subs || [];
  useEffect(() => {
    // sync URL ?tab= with current sub (skip default overview to keep URL clean)
    const next = new URLSearchParams(searchParams);
    if (sub && sub !== "overview") next.set("tab", sub); else next.delete("tab");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub]);
  const goGroup = (gKey) => {
    const g = GROUPS.find((gr) => gr.key === gKey);
    if (g && g.subs.length) setSub(g.subs[0]);
  };

  const load = async () => {
    const promises = [
      api.get(`/bookings/${id}`), api.get("/tasks"),
      api.get(`/bookings/${id}/activities`),
      api.get(`/booking-lines?booking_id=${id}`).catch(() => ({ data: [] })),
      api.get("/products?active_only=true").catch(() => ({ data: [] })),
    ];
    if (financial) promises.push(api.get(`/bookings/${id}/finance`));
    const results = await Promise.all(promises);
    setB(results[0].data);
    setTasks(results[1].data.filter((t) => t.booking_id === id));
    setActivities(results[2].data);
    setLines(results[3].data || []);
    setProducts(results[4].data || []);
    if (financial && results[5]) { setFinance(results[5].data); setPayments(results[5].data.payments || []); }
    try { const { data } = await api.get(`/bookings/${id}/content`); setLinkedContent(data || []); }
    catch (_) { setLinkedContent([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!b) return <div className="text-sm text-[#8C9096]">Memuat pemesanan...</div>;

  const days = daysUntil(b.event_date);
  const dateBadge = days === null ? "" : days >= 0 ? `H-${days}` : `H+${Math.abs(days)}`;

  const updateService = async (svc, updates) => {
    try {
      await api.put(`/bookings/${id}/services/${svc.id}`, updates);
      toast.success("Layanan diperbarui");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/bookings/${id}/notes`, { note: noteText });
    setNoteText(""); toast.success("Catatan ditambahkan"); load();
  };

  return (
    <div className="space-y-6" data-testid="booking-detail-page">      <div className="flex items-center gap-3">
        <Link to="/bookings" className="text-sm text-[#5C6066] hover:text-brand inline-flex items-center gap-1" data-testid="link-back-bookings">
          <ChevronLeft className="w-4 h-4" /> Kembali
        </Link>
      </div>

      <Card className="p-6 border border-[#E5E0D8] bg-white shadow-none">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-brand">{b.booking_code}</span>
              <StatusBadge status={b.status} label={BOOKING_STATUS_LABEL[b.status]} testId="booking-status-badge" />
              {financial && b.payment_status && <StatusBadge status={b.payment_status} />}
              {!financial && (
                <StatusBadge status={b.admin_clear ? "Done" : "Waiting"} label={b.admin_clear ? "Administrasi Clear" : "Belum Clear"} />
              )}
            </div>
            <h1 className="font-display text-2xl font-bold mt-2">{b.event_name}</h1>
            <div className="text-sm text-[#5C6066] mt-1">Klien: <span className="font-medium text-[#1E2022]">{clientName(b.client)}</span></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right mr-4">
              <div className="text-xs uppercase tracking-wide text-[#8C9096]">Tanggal Acara</div>
              <div className="font-display text-lg font-semibold">{formatDateLong(b.event_date)}</div>
              <div className="text-xs text-brand font-semibold">{dateBadge}</div>
            </div>
            {isManagement && (
              <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="btn-edit-booking">
                <Pencil className="w-4 h-4 mr-1.5" /> Edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#F0EBE0]">
          <Info icon={MapPin} label="Venue" value={b.venue || "-"} sub={b.location} />
          <Info icon={UsersIcon} label="Tamu" value={b.guest_count || 0} />
          <Info icon={CalIcon} label="Mulai" value={b.start_time || "-"} sub={financial ? b.package_name : undefined} />
          {financial ? (
            <Info icon={Wallet} label="Nilai Final" value={formatIDR(b.final_value)} sub={`Sisa: ${formatIDR(b.remaining)}`} />
          ) : (
            <Info icon={Wallet} label="Administrasi" value={b.admin_clear ? "Clear" : "Belum Clear"} />
          )}
        </div>
        {financial && (
          <>
            <Progress value={b.percentage || 0} className="mt-6 h-2" data-testid="payment-progress" />
            <div className="flex justify-between text-xs text-[#5C6066] mt-1.5">
              <span>Terbayar {formatIDR(b.total_paid)}</span>
              <span>{b.percentage || 0}%</span>
            </div>
          </>
        )}
      </Card>

      {/* Quick actions above-fold */}
      <Card className="p-4 sm:p-5 border border-[#E5E0D8] bg-gradient-to-br from-[#FAF6EE] to-white shadow-none" data-testid="booking-quick-actions">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">Aksi Cepat</div>
            <div className="text-xs text-[#5C6066] mt-0.5">
              {b.status === "Inquiry" && "Langkah selanjutnya: catat pembayaran booking fee agar bisa naik ke Tentative."}
              {b.status === "Tentative" && "Langkah selanjutnya: atur tim & tambahkan tugas persiapan divisi."}
              {b.status === "Confirmed" && "Booking sudah dikonfirmasi. Lengkapi file, tugas, dan pembayaran final."}
              {b.status === "Completed" && "Acara sudah selesai. Selesaikan pembayaran atau evidence yang tertunda."}
              {b.status === "Cancelled" && "Booking dibatalkan. Data tetap tersimpan untuk arsip."}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {isManagement && (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="qa-edit"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
            )}
            {financial && (
              <Button size="sm" onClick={() => setPayOpen(true)} className="bg-brand text-white hover:bg-brand/90" data-testid="qa-add-payment"><Wallet className="w-3.5 h-3.5 mr-1.5" /> Catat Pembayaran</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => { setSub("team"); }} data-testid="qa-manage-team"><UserPlus className="w-3.5 h-3.5 mr-1.5" /> Atur Tim</Button>
            <Button size="sm" variant="outline" onClick={() => setTaskOpen(true)} data-testid="qa-add-task"><Plus className="w-3.5 h-3.5 mr-1.5" /> Tambah Tugas</Button>
            <Button size="sm" variant="outline" onClick={() => { setSub("team"); }} data-testid="qa-add-file"><FileText className="w-3.5 h-3.5 mr-1.5" /> Tambah File</Button>
          </div>
        </div>
      </Card>

      {/* Timeline 7-stage editable */}
      <BookingTimelineStrip bookingId={id} canEdit={isManagement || canOrder} />

      <Tabs value={sub} onValueChange={setSub}>
        {/* Row 1 — Primary 4 groups */}
        <div className="flex flex-wrap gap-1 bg-white border border-[#E5E0D8] rounded-lg p-1" role="tablist" data-testid="booking-group-nav">
          {GROUPS.filter((g) => g.subs.length).map((g) => {
            const active = activeGroup === g.key;
            return (
              <button key={g.key}
                onClick={() => goGroup(g.key)}
                aria-selected={active}
                className={`px-3 sm:px-4 h-9 rounded-md text-xs sm:text-sm font-semibold transition-colors ${active ? "bg-brand text-white shadow-sm" : "text-[#5C6066] hover:bg-[#FAF6EE]"}`}
                data-testid={`booking-group-${g.key}`}>{g.label}</button>
            );
          })}
        </div>
        {/* Row 2 — Sub-tabs of active group (only if group has >1 sub) */}
        {activeSubs.length > 1 && (
          <TabsList className="bg-transparent border-b border-[#E5E0D8] flex-wrap h-auto rounded-none px-0 mt-3 gap-1">
            {activeSubs.map((s) => (
              <TabsTrigger key={s} value={s} data-testid={`booking-tab-${s}`}
                className="data-[state=active]:bg-[#FAF6EE] data-[state=active]:text-brand data-[state=active]:border-b-2 data-[state=active]:border-brand rounded-none px-3">
                {SUB_LABELS[s]}
              </TabsTrigger>
            ))}
          </TabsList>
        )}

        <TabsContent value="overview" className="mt-4">
          <Card className="p-6 border border-[#E5E0D8] bg-white shadow-none space-y-4">
            <h3 className="font-display font-semibold">Ringkasan Umum</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <KV k="Klien" v={clientName(b.client)} />
              <KV k="WhatsApp" v={b.client?.whatsapp || "-"} />
              <KV k="Instagram" v={b.client?.instagram || "-"} />
              <KV k="Sumber Lead" v={b.client?.lead_source || "-"} />
              <KV k="Admin PIC" v={b.assigned_admin || "-"} />
              <KV k="Paket" v={b.package_name || "-"} />
              <KV k="Total Kontrak" v={formatIDR(b.total_value)} />
              <KV k="Diskon" v={formatIDR(b.discount)} />
            </div>
            {b.notes && (
              <div>
                <div className="text-xs uppercase tracking-wide text-[#8C9096] mb-1">Catatan</div>
                <p className="text-sm text-[#1E2022] whitespace-pre-wrap">{b.notes}</p>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="orders" className="mt-4 space-y-4">
          {b.orders_locked && (
            <Card className="p-3 border border-amber-300 bg-amber-50 flex items-center gap-2 text-sm text-amber-900" data-testid="orders-locked-notice">
              <Lock className="w-4 h-4"/> Pemesanan terkunci — {b.orders_locked_reason || "Sudah ada quotation diterima / invoice terbit"}
            </Card>
          )}
          <div className="flex items-center justify-between">
            <div className="text-sm text-[#5C6066]">Master Pemesanan client (paket utama, add-on, layanan custom). Harga di-snapshot dari Master Pricelist saat ditambahkan.</div>
            <div className="flex gap-2">
              {isManagement && lines.length > 0 && (
                <Button variant="outline" onClick={async () => { try { const { data } = await api.post(`/quotations/from-booking/${id}`); toast.success(`Penawaran ${data.quotation_code} dibuat`); window.open(`/print/quotation/${data.id}`, "_blank"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} data-testid="btn-auto-quotation">
                  <FileText className="w-4 h-4 mr-1.5"/> Auto-Buat Penawaran
                </Button>
              )}
              {canOrder && !b.orders_locked && (
                <Button className="bg-brand text-white" onClick={() => setAddLineOpen(true)} data-testid="btn-add-order-line">
                  <Plus className="w-4 h-4 mr-1.5"/> Tambah Item
                </Button>
              )}
            </div>
          </div>
          <Card className="border border-[#E5E0D8] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                  <th className="text-left px-4 py-3">Item</th>
                  <th className="text-left px-4 py-3">Tipe</th>
                  <th className="text-center px-4 py-3">Qty</th>
                  {financial && <th className="text-right px-4 py-3">Harga</th>}
                  {financial && <th className="text-right px-4 py-3">Total</th>}
                  <th className="text-left px-4 py-3">Ditambahkan</th>
                  {canOrder && <th className="text-right px-4 py-3">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && <tr><td colSpan={financial ? 7 : 5} className="text-center py-8 text-[#8C9096]">Belum ada item pemesanan.</td></tr>}
                {lines.map((l) => (
                  <tr key={l.id} className="border-t border-[#F0EBE0]" data-testid={`order-line-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.product_snapshot?.name}</div>
                      {l.product_snapshot?.description && <div className="text-[11px] text-[#8C9096] mt-0.5">{l.product_snapshot.description}</div>}
                      {l.product_snapshot?.included?.length > 0 && (
                        <div className="text-[11px] text-[#5C6066] mt-1">{l.product_snapshot.included.map((i) => i.description || i.division_id).join(" • ")}</div>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={l.line_type === "package" ? "Accepted" : l.line_type === "addon" ? "Sent" : "Draft"} label={l.line_type} /></td>
                    <td className="px-4 py-3 text-center">{l.quantity} {l.product_snapshot?.unit || ""}</td>
                    {financial && <td className="px-4 py-3 text-right font-mono">{formatIDR(l.product_snapshot?.unit_price || 0)}</td>}
                    {financial && <td className="px-4 py-3 text-right font-mono font-bold">{formatIDR(l.line_total || 0)}</td>}
                    <td className="px-4 py-3 text-[11px] text-[#8C9096]">{formatDate(l.created_at)}<br/>{l.created_by}</td>
                    {canOrder && <td className="px-4 py-3 text-right">
                      {!b.orders_locked && <Button variant="ghost" size="icon" onClick={async () => { if (!window.confirm("Hapus item ini?")) return; try { await api.delete(`/booking-lines/${l.id}`); toast.success("Item dihapus"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} data-testid={`btn-delete-line-${l.id}`}><Trash2 className="w-4 h-4"/></Button>}
                    </td>}
                  </tr>
                ))}
                {financial && lines.length > 0 && (
                  <tr className="bg-[#FAF6EE] border-t border-[#E5E0D8]">
                    <td colSpan={4} className="px-4 py-3 text-right font-semibold">Total Pemesanan (Snapshot)</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-brand" data-testid="orders-total">{formatIDR(lines.reduce((a, l) => a + (l.line_total || 0), 0))}</td>
                    <td colSpan={canOrder ? 2 : 1}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="services" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display font-semibold">Divisi / Vendor Terlibat ({(b.services || []).length})</div>
              <div className="text-[11px] text-[#8C9096]">Kelola divisi yang mengerjakan pemesanan ini. Paket dapat dipilih dari Master Pricelist.</div>
            </div>
            {["owner","admin","general_manager","division_manager"].includes(user?.role) && (
              <Button size="sm" className="bg-brand text-white" onClick={() => setAddServiceOpen(true)} data-testid="btn-add-service">
                <Plus className="w-4 h-4 mr-1"/>Tambah Divisi
              </Button>
            )}
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {(b.services || []).length === 0 && (
              <Card className="p-6 border border-[#E5E0D8] bg-white col-span-full text-center text-[#8C9096]">
                Belum ada divisi terpilih.
              </Card>
            )}
            {(b.services || []).map((s) => {
              const d = divisionById(s.division_id) || {};
              const canManageDiv = ["owner","admin","general_manager"].includes(user?.role)
                || (user?.role === "division_admin" && user?.division_id === "div-mua")
                || (user?.role === "division_manager" && user?.division_id === s.division_id);
              return (
                <Card key={s.id} className="p-5 border border-[#E5E0D8] bg-white shadow-none" data-testid={`service-card-${s.division_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold border ${d.color || "bg-zinc-100"}`}>{d.short}</div>
                      <h4 className="font-display font-semibold text-base mt-2">{s.package_name || d.name || s.division_name}</h4>
                      {s.package_price ? <div className="text-[11px] text-[#8C9096]">{formatIDR(s.package_price)}</div> : null}
                    </div>
                    <div className="flex items-center gap-1">
                      {canManageDiv && <button onClick={() => setEditService(s)} className="p-1.5 rounded hover:bg-[#FAF6EE]" title="Edit paket" data-testid={`btn-edit-service-${s.division_id}`}><Pencil className="w-3.5 h-3.5 text-[#5C6066]"/></button>}
                      {canManageDiv && <button onClick={() => setDeleteService(s)} className="p-1.5 rounded hover:bg-rose-50" title="Hapus divisi" data-testid={`btn-delete-service-${s.division_id}`}><Trash2 className="w-3.5 h-3.5 text-rose-600"/></button>}
                      <StatusBadge status={s.status} label={SERVICE_STATUS_LABEL[s.status]} />
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-center gap-1.5 text-[#5C6066]"><User className="w-3.5 h-3.5" /> PIC: <span className="text-[#1E2022] font-medium">{s.pic || "-"}</span></div>
                    <div className="flex items-center gap-1.5 text-[#5C6066]"><ArrowRight className="w-3.5 h-3.5" /> {s.next_action || "-"}</div>
                    <div className="flex items-center gap-1.5 text-[#5C6066]"><Clock className="w-3.5 h-3.5" /> {formatDate(s.next_action_date)}</div>
                  </div>
                  <Progress value={s.progress || 0} className="mt-4 h-1.5" />
                  <div className="text-[11px] text-[#8C9096] mt-1">{s.progress || 0}%</div>
                  <div className="mt-4">
                    <Select value={s.status} onValueChange={(v) => updateService(s, { status: v })}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`service-status-${s.division_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SERVICE_STATUSES.map((st) => <SelectItem key={st} value={st}>{SERVICE_STATUS_LABEL[st]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-[#5C6066]">
                Sisa Tagihan <span className="font-display text-lg font-bold text-[#1E2022] ml-2" data-testid="payment-remaining-balance">{formatIDR(b.remaining)}</span>
              </div>
            </div>
            <PaymentDialog open={payOpen} setOpen={setPayOpen} bookingId={id} onDone={load} />
          </div>
          <Card className="border border-[#E5E0D8] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                  <th className="text-left px-5 py-3">Tanggal</th>
                  <th className="text-left px-4 py-3">Jenis</th>
                  <th className="text-left px-4 py-3">Metode</th>
                  <th className="text-right px-4 py-3">Nominal</th>
                  <th className="text-left px-4 py-3">Catatan</th>
                  <th className="text-left px-4 py-3">Dicatat oleh</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#8C9096]">Belum ada pembayaran.</td></tr>}
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-[#F0EBE0]">
                    <td className="px-5 py-3">{formatDate(p.payment_date)}</td>
                    <td className="px-4 py-3">{p.payment_type}</td>
                    <td className="px-4 py-3 text-[#5C6066]">{p.method}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatIDR(p.amount)}</td>
                    <td className="px-4 py-3 text-[#5C6066]">{p.notes || "-"}</td>
                    <td className="px-4 py-3 text-[#8C9096] text-xs">{p.recorded_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="finance" className="mt-4 space-y-4">
          {finance && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniStat label="Total Kontrak" value={formatIDR(finance.final_value)} />
                <MiniStat label="Diterima" value={formatIDR(finance.total_paid)} tone="success" />
                <MiniStat label="Sisa Tagihan" value={formatIDR(finance.remaining)} tone="danger" />
                <MiniStat label="Total Invoice" value={formatIDR(finance.total_invoiced)} />
                <MiniStat label="Vendor Total" value={formatIDR(finance.vendor_total)} />
                <MiniStat label="Vendor Terbayar" value={formatIDR(finance.vendor_paid)} tone="success" />
                <MiniStat label="Fee Crew Total" value={formatIDR(finance.wage_total)} />
                <MiniStat label="Fee Crew Terbayar" value={formatIDR(finance.wage_paid)} tone="success" />
              </div>
              <Card className="border border-[#E5E0D8] bg-white">
                <div className="px-5 py-3 border-b border-[#E5E0D8] font-display font-semibold">Vendor Payables</div>
                <table className="w-full text-sm">
                  <thead><tr className="bg-[#FAF6EE] text-xs uppercase text-[#8C9096]"><th className="text-left p-3">Vendor</th><th className="text-left p-3">Deskripsi</th><th className="text-right p-3">Nominal</th><th className="text-left p-3">Status</th></tr></thead>
                  <tbody>
                    {(finance.vendor_payables || []).length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[#8C9096]">-</td></tr>}
                    {(finance.vendor_payables || []).map((p) => (
                      <tr key={p.id} className="border-t border-[#F0EBE0]"><td className="p-3">{p.vendor_name}</td><td className="p-3">{p.description}</td><td className="p-3 text-right font-mono">{formatIDR(p.amount)}</td><td className="p-3"><StatusBadge status={p.status} /></td></tr>
                    ))}
                  </tbody>
                </table>
              </Card>
              <Card className="border border-[#E5E0D8] bg-white">
                <div className="px-5 py-3 border-b border-[#E5E0D8] font-display font-semibold">Fee Crew</div>
                <table className="w-full text-sm">
                  <thead><tr className="bg-[#FAF6EE] text-xs uppercase text-[#8C9096]"><th className="text-left p-3">Nama</th><th className="text-left p-3">Role</th><th className="text-right p-3">Fee Akhir</th><th className="text-left p-3">Status</th></tr></thead>
                  <tbody>
                    {(finance.job_wages || []).length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[#8C9096]">-</td></tr>}
                    {(finance.job_wages || []).map((w) => (
                      <tr key={w.id} className="border-t border-[#F0EBE0]"><td className="p-3">{w.staff_name}</td><td className="p-3">{w.role}</td><td className="p-3 text-right font-mono">{formatIDR((w.base_fee||0)+(w.additional_fee||0)-(w.deduction||0))}</td><td className="p-3"><StatusBadge status={w.status} /></td></tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="tasks" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <TaskDialog open={taskOpen} setOpen={setTaskOpen} bookingId={id} divisions={b.services || []} onDone={load} />
          </div>
          <div className="space-y-2">
            {tasks.length === 0 && <div className="text-sm text-[#8C9096] text-center py-8">Belum ada tugas.</div>}
            {tasks.map((t) => (
              <Card key={t.id} className="p-4 border border-[#E5E0D8] bg-white flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{t.title}</div>
                    <StatusBadge status={t.priority} label={TASK_PRIORITY_LABEL[t.priority]} />
                  </div>
                  <div className="text-xs text-[#8C9096] mt-1">Jatuh tempo {formatDate(t.due_date)} • {t.assigned_staff || "Belum ditugaskan"}</div>
                </div>
                <Select value={t.status} onValueChange={async (v) => { await api.put(`/tasks/${t.id}`, { status: v }); toast.success("Tugas diperbarui"); load(); }}>
                  <SelectTrigger className="w-40 h-9 text-sm" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-4 space-y-4">
          <Card className="p-4 border border-[#E5E0D8] bg-white">
            <Label>Tambah Catatan Tim</Label>
            <Textarea className="mt-1.5" rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)}
              placeholder="Tulis catatan atau update tim..." data-testid="note-input" />
            <div className="mt-2 flex justify-end">
              <Button onClick={addNote} className="bg-brand text-white" data-testid="btn-add-note">Simpan Catatan</Button>
            </div>
          </Card>
          <Card className="border border-[#E5E0D8] bg-white">
            <div className="px-5 py-3 border-b border-[#E5E0D8] font-display font-semibold text-sm">Riwayat Aktivitas</div>
            <div className="divide-y divide-[#F0EBE0]">
              {activities.length === 0 && <div className="p-6 text-sm text-[#8C9096] text-center">Belum ada aktivitas.</div>}
              {activities.map((a) => (
                <div key={a.id} className="px-5 py-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center text-xs font-semibold text-brand shrink-0">
                    {(a.user_name || "U").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-medium">{a.user_name}</span> — {a.activity}
                      {a.old_value && a.new_value && (
                        <span className="text-[#8C9096]"> ({String(a.old_value)} → {String(a.new_value)})</span>
                      )}
                    </div>
                    {a.note && <div className="text-xs text-[#5C6066] mt-0.5">{a.note}</div>}
                    <div className="text-[11px] text-[#8C9096] mt-1">{new Date(a.created_at).toLocaleString("id-ID")}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-4 hidden">
          {/* Deprecated — kept as hidden alias so old deep-links (?tab=documents) still mount. Content moved to 'team'. */}
        </TabsContent>

        <TabsContent value="team" className="mt-4 space-y-4">
          <div className="flex items-start gap-2 p-3 rounded-md bg-[#FAF6EE] border border-[#EAE3D6]">
            <UserPlus className="w-4 h-4 text-brand mt-0.5" />
            <div className="text-xs text-[#5C6066]">
              <b>Tim & File — satu tempat</b> untuk assign koordinator/crew dan unggah bukti (foto, PDF, dokumen kontrak, moodboard, rundown).
            </div>
          </div>
          <Card className="p-5 border border-[#E5E0D8] bg-white">
            <div className="text-sm font-semibold mb-3 flex items-center gap-1.5"><UserPlus className="w-4 h-4"/> Tim / Koordinator</div>
            <CoordinatorAssign bookingId={id} canEdit={["owner","admin","general_manager","division_manager"].includes(user?.role)} />
          </Card>
          <Card className="p-5 border border-[#E5E0D8] bg-white">
            <div className="text-sm font-semibold mb-3 flex items-center gap-1.5"><FileText className="w-4 h-4"/> File & Dokumen</div>
            <FileUpload bookingId={id} category="Evidence"/>
          </Card>
        </TabsContent>

        <TabsContent value="content" className="mt-4 space-y-3">
          <Card className="p-5 border border-[#E5E0D8] bg-white">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold">Konten Terhubung ({linkedContent.length})</div>
                <div className="text-[11px] text-[#8C9096]">Semua rencana konten yang terhubung ke acara ini.</div>
              </div>
              <Link to="/content" className="text-brand hover:underline text-sm">Kelola Ruang Kerja Konten →</Link>
            </div>
            {linkedContent.length === 0 && (
              <div className="text-sm text-[#8C9096] italic py-6 text-center">Belum ada konten terhubung. Buka <Link to="/content" className="text-brand hover:underline">Ruang Kerja Konten</Link>, buka salah satu konten → tab <b>Event</b> → hubungkan ke booking ini.</div>
            )}
            {linkedContent.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {linkedContent.map((k) => (
                  <button key={k.id} onClick={() => setOpenContentId(k.id)} className="text-left border border-[#EAE3D6] rounded p-3 bg-white hover:bg-[#FAF6EE]" data-testid={`booking-content-${k.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAF6EE] border border-[#EAE3D6]">{k.platform} • {k.content_type}</span>
                      <span className="text-[10px] font-semibold text-brand">{BOOKING_STATUS_LABEL[k.status] || k.status}</span>
                    </div>
                    <div className="text-sm font-medium mt-1">{k.title}</div>
                    <div className="text-[11px] text-[#8C9096] mt-1">
                      {k.creator_name || "Belum di-assign"}
                      {k.planned_date && ` • Tayang ${formatDate(k.planned_date)}`}
                      {k.deadline && ` • DL ${formatDate(k.deadline)}`}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(k.must_moments || []).length > 0 && <span className="text-[9px] px-1 rounded bg-rose-50 text-rose-700 border border-rose-200">{k.must_moments.filter((m) => m.done).length}/{k.must_moments.length} momen wajib</span>}
                      {(k.live_prep || []).length > 0 && <span className="text-[9px] px-1 rounded bg-sky-50 text-sky-700 border border-sky-200">{k.live_prep.filter((m) => m.done).length}/{k.live_prep.length} live prep</span>}
                      {k.same_day_delivery && <span className="text-[9px] px-1 rounded bg-amber-50 text-amber-800 border border-amber-200">SAME-DAY</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
          <ContentDetailModal contentId={openContentId} open={!!openContentId} onOpenChange={(v) => { if (!v) { setOpenContentId(null); load(); } }} onChanged={load}/>
        </TabsContent>

        <TabsContent value="ops" className="mt-4 space-y-3">
          <OpsDivisiTabs booking={b} user={user}/>
        </TabsContent>
      </Tabs>

      <EditBookingDialog open={editOpen} setOpen={setEditOpen} booking={b} onDone={load} />
      <AddOrderLineDialog open={addLineOpen} setOpen={setAddLineOpen} bookingId={id} products={products} userRole={user?.role} userDivision={user?.division_id} onDone={load} />
      <AddServiceDialog open={addServiceOpen} setOpen={setAddServiceOpen} bookingId={id} existingDivisions={(b.services || []).map((s) => s.division_id)} userRole={user?.role} userDivision={user?.division_id} onDone={load}/>
      <EditServiceDialog service={editService} setService={setEditService} bookingId={id} onDone={load}/>
      <DeleteServiceDialog service={deleteServiceState} setService={setDeleteService} bookingId={id} onDone={load}/>
    </div>
  );
}

const AddServiceDialog = ({ open, setOpen, bookingId, existingDivisions, userRole, userDivision, onDone }) => {
  const [divisionId, setDivisionId] = useState("");
  const [productId, setProductId] = useState("");
  const [manualName, setManualName] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [pic, setPic] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const isCentral = ["owner","admin","general_manager"].includes(userRole);
  const availableDivisions = (typeof window !== "undefined" ? null : null) || null;
  // load DIVISIONS from constants
  const DIVS = require("../lib/constants").DIVISIONS;
  const divs = DIVS.filter((d) => !existingDivisions.includes(d.id))
    .filter((d) => isCentral || d.id === userDivision);
  useEffect(() => {
    if (!open) { setDivisionId(""); setProductId(""); setManualName(""); setManualPrice(""); setPic(""); }
  }, [open]);
  useEffect(() => {
    if (!divisionId) { setProducts([]); return; }
    setLoading(true);
    api.get(`/products?active_only=true`)
      .then(({ data }) => {
        // Multi-division packages (division_id null, kind=package) + division-specific products/packages
        const filtered = (data || []).filter((p) => !p.division_id || p.division_id === divisionId);
        setProducts(filtered);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [divisionId]);
  const submit = async () => {
    if (!divisionId) return toast.error("Pilih divisi");
    try {
      const payload = { division_id: divisionId, pic };
      if (productId && productId !== "__manual__") payload.product_id = productId;
      if (manualName) payload.package_name = manualName;
      if (manualPrice !== "") payload.package_price = Number(manualPrice);
      await api.post(`/bookings/${bookingId}/services`, payload);
      toast.success("Divisi ditambahkan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dwgPackages = products.filter((p) => p.kind === "package" && !p.division_id);
  const divPackages = products.filter((p) => p.kind === "package" && p.division_id);
  const divProducts = products.filter((p) => p.kind !== "package");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="add-service-dialog">
        <DialogHeader><DialogTitle>Tambah Divisi ke Pemesanan</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Divisi</Label>
            <Select value={divisionId} onValueChange={setDivisionId}>
              <SelectTrigger className="mt-1" data-testid="add-service-division"><SelectValue placeholder="Pilih divisi"/></SelectTrigger>
              <SelectContent>{divs.map((d) => <SelectItem key={d.id} value={d.id}>{d.short} — {d.name}</SelectItem>)}</SelectContent>
            </Select>
            {divs.length === 0 && <div className="text-[11px] text-amber-700 mt-1">Semua divisi sudah terdaftar.</div>}
          </div>
          {divisionId && (
            <div>
              <Label>Paket (dari Master Pricelist)</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger className="mt-1" data-testid="add-service-package"><SelectValue placeholder={loading ? "Memuat…" : "Pilih paket / satuan / kosongkan"}/></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="__manual__">— Tanpa paket / isi manual —</SelectItem>
                  {dwgPackages.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Dessy Wedding Group (multi-divisi)</div>}
                  {dwgPackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
                  {divPackages.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Divisi</div>}
                  {divPackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
                  {divProducts.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Satuan (per vendor)</div>}
                  {divProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {(productId === "__manual__" || (divisionId && !productId)) && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nama Paket (manual)</Label><Input className="mt-1" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Opsional" data-testid="add-service-manual-name"/></div>
              <div><Label>Harga (Rp)</Label><Input className="mt-1" type="number" value={manualPrice} onChange={(e) => setManualPrice(e.target.value)} placeholder="0"/></div>
            </div>
          )}
          <div><Label>PIC (opsional)</Label><Input className="mt-1" value={pic} onChange={(e) => setPic(e.target.value)}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button className="bg-brand text-white" onClick={submit} disabled={!divisionId} data-testid="btn-save-add-service">Tambahkan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditServiceDialog = ({ service, setService, bookingId, onDone }) => {
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [pic, setPic] = useState("");
  const [products, setProducts] = useState([]);
  const open = !!service;
  useEffect(() => {
    if (!service) return;
    setProductId(service.package_snapshot?.id || "");
    setName(service.package_name || "");
    setPrice(service.package_price != null ? String(service.package_price) : "");
    setPic(service.pic || "");
    api.get(`/products?active_only=true`)
      .then(({ data }) => setProducts((data || []).filter((p) => !p.division_id || p.division_id === service.division_id)))
      .catch(() => setProducts([]));
  }, [service]);
  const submit = async () => {
    try {
      const payload = {};
      if (productId && productId !== "__manual__") payload.product_id = productId;
      if (name) payload.package_name = name;
      if (price !== "") payload.package_price = Number(price);
      payload.pic = pic;
      await api.put(`/bookings/${bookingId}/services/${service.id}`, payload);
      toast.success("Paket diperbarui"); setService(null); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dwgPackages = products.filter((p) => p.kind === "package" && !p.division_id);
  const divPackages = products.filter((p) => p.kind === "package" && p.division_id);
  const divProducts = products.filter((p) => p.kind !== "package");
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setService(null); }}>
      <DialogContent className="max-w-lg" data-testid="edit-service-dialog">
        <DialogHeader><DialogTitle>Edit Paket Divisi {service?.division_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Paket (dari Master Pricelist)</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="mt-1" data-testid="edit-service-package"><SelectValue placeholder="Pilih paket / satuan"/></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__manual__">— Tanpa paket / isi manual —</SelectItem>
                {dwgPackages.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Dessy Wedding Group</div>}
                {dwgPackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
                {divPackages.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Divisi</div>}
                {divPackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
                {divProducts.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Satuan</div>}
                {divProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Nama Paket (override)</Label><Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)}/></div>
            <div><Label>Harga (Rp)</Label><Input className="mt-1" type="number" value={price} onChange={(e) => setPrice(e.target.value)}/></div>
          </div>
          <div><Label>PIC</Label><Input className="mt-1" value={pic} onChange={(e) => setPic(e.target.value)}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setService(null)}>Batal</Button>
          <Button className="bg-brand text-white" onClick={submit} data-testid="btn-save-edit-service">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteServiceDialog = ({ service, setService, bookingId, onDone }) => {
  const open = !!service;
  const submit = async () => {
    try {
      await api.delete(`/bookings/${bookingId}/services/${service.id}`);
      toast.success("Divisi dihapus"); setService(null); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setService(null); }}>
      <DialogContent className="max-w-sm" data-testid="delete-service-dialog">
        <DialogHeader><DialogTitle>Hapus divisi ini?</DialogTitle></DialogHeader>
        <div className="text-sm py-2">Divisi <b>{service?.division_name}</b> beserta paketnya akan dihapus dari pemesanan. Tugas / workspace divisi tidak ikut terhapus.</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setService(null)}>Batal</Button>
          <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={submit} data-testid="btn-confirm-delete-service">Hapus</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Ops Divisi — sub-tabs per divisi yang punya layanan di booking ini.
const OpsDivisiTabs = ({ booking, user }) => {
  const services = booking?.services || [];
  const availableKinds = services
    .map((s) => SW_KIND_BY_DIVISION[s.division_id])
    .filter(Boolean);
  const kinds = Array.from(new Set(availableKinds));
  const [active, setActive] = useState(kinds[0] || "");
  useEffect(() => { if (!active && kinds[0]) setActive(kinds[0]); /* eslint-disable-next-line */ }, [kinds[0]]);
  if (kinds.length === 0) {
    return (
      <Card className="p-6 border border-[#E5E0D8] bg-white text-center text-sm text-[#8C9096]" data-testid="ops-empty">
        Booking ini belum memiliki layanan divisi yang punya modul workspace.
        <div className="text-[11px] mt-1">Divisi yang didukung: MUA, Dekor, PBH, Snap, WO, Musical, Attire, Handcraft.</div>
      </Card>
    );
  }
  const isCentral = ["owner", "admin", "general_manager"].includes(user?.role)
    || (user?.role === "division_admin" && user?.division_id === "div-mua");
  const canEditKind = (kind) => {
    if (isCentral) return true;
    const s = SW_SCHEMAS[kind];
    if (!s) return false;
    return user?.division_id === s.division_id;
  };
  return (
    <div>
      <div className="flex gap-1 flex-wrap border-b border-[#EAE3D6] mb-3">
        {kinds.map((k) => {
          const s = SW_SCHEMAS[k];
          const disabled = !isCentral && user?.division_id !== s.division_id;
          return (
            <button key={k} disabled={disabled} onClick={() => setActive(k)}
              className={`px-3 py-1.5 text-xs border-b-2 -mb-px transition ${active === k ? "border-brand text-brand font-semibold" : "border-transparent text-[#5C6066] hover:text-[#1E2022]"} disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`ops-subtab-${k}`}>
              {s.label}
            </button>
          );
        })}
      </div>
      {active && SW_SCHEMAS[active] && (
        <ServiceWorkspacePanel bookingId={booking.id} schema={SW_SCHEMAS[active]} canEdit={canEditKind(active)}/>
      )}
    </div>
  );
};

const AddOrderLineDialog = ({ open, setOpen, bookingId, products, userRole, userDivision, onDone }) => {
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [lineType, setLineType] = useState("addon");
  const [overrideName, setOverrideName] = useState("");
  const [overridePrice, setOverridePrice] = useState("");
  const eligibleProducts = products.filter((p) => {
    if (userRole === "division_manager" || userRole === "division_admin") return p.division_id === userDivision;
    return true;
  });
  const submit = async () => {
    if (!productId) { toast.error("Pilih produk dari Master Pricelist"); return; }
    try {
      const payload = { booking_id: bookingId, product_id: productId, quantity: Number(quantity), line_type: lineType };
      if (overrideName) payload.override_name = overrideName;
      if (overridePrice !== "") payload.override_price = Number(overridePrice);
      await api.post("/booking-lines", payload);
      toast.success("Item pemesanan ditambahkan"); setOpen(false); onDone();
      setProductId(""); setQuantity(1); setOverrideName(""); setOverridePrice(""); setLineType("addon");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="modal-add-order-line">
        <DialogHeader><DialogTitle>Tambah Item Pemesanan</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Produk / Paket dari Master Pricelist</Label>
            <Select value={productId} onValueChange={(v) => { setProductId(v); const p = eligibleProducts.find((x) => x.id === v); if (p && !overrideName) setOverrideName(p.name); }}>
              <SelectTrigger className="mt-1.5" data-testid="order-line-product"><SelectValue placeholder="Pilih produk..." /></SelectTrigger>
              <SelectContent className="max-h-72">
                {eligibleProducts.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.kind === "package" ? "📦" : "🛍"} {p.name} — {formatIDR(p.price)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipe Line</Label>
            <Select value={lineType} onValueChange={setLineType}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="package">Paket Utama</SelectItem>
                <SelectItem value="addon">Add-on</SelectItem>
                <SelectItem value="custom">Custom / Division-specific</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Qty</Label><Input type="number" min="1" className="mt-1.5" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="order-line-qty"/></div>
            <div><Label>Override Harga (opsional)</Label><Input type="number" className="mt-1.5" value={overridePrice} onChange={(e) => setOverridePrice(e.target.value)} placeholder="Kosongkan = harga master"/></div>
          </div>
          <div><Label>Override Nama (opsional)</Label><Input className="mt-1.5" value={overrideName} onChange={(e) => setOverrideName(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button className="bg-brand text-white" onClick={submit} data-testid="btn-save-order-line">Tambah + Snapshot</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MiniStat = ({ label, value, tone }) => (
  <div className="p-3 rounded-md border border-[#E5E0D8] bg-white">
    <div className="text-[10px] uppercase tracking-wider text-[#8C9096]">{label}</div>
    <div className={`mt-1 font-mono text-sm font-bold ${tone === "success" ? "text-emerald-700" : tone === "danger" ? "text-rose-700" : "text-[#1E2022]"}`}>{value}</div>
  </div>
);

const Info = ({ icon: Icon, label, value, sub }) => (
  <div>
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#8C9096]"><Icon className="w-3.5 h-3.5" />{label}</div>
    <div className="mt-1 font-medium">{value}</div>
    {sub && <div className="text-xs text-[#8C9096]">{sub}</div>}
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex justify-between border-b border-[#F0EBE0] py-2">
    <span className="text-[#8C9096]">{k}</span>
    <span className="font-medium text-right">{v}</span>
  </div>
);

const PaymentDialog = ({ open, setOpen, bookingId, onDone }) => {
  const [f, setF] = useState({ payment_date: new Date().toISOString().slice(0, 10), payment_type: "DP 1", amount: 0, method: "Transfer", notes: "" });
  const submit = async () => {
    try {
      await api.post("/payments", { booking_id: bookingId, ...f, amount: Number(f.amount) });
      toast.success("Pembayaran dicatat"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand text-white" data-testid="btn-add-payment"><Plus className="w-4 h-4 mr-1.5" /> Catat Pembayaran</Button>
      </DialogTrigger>
      <DialogContent data-testid="modal-new-payment">
        <DialogHeader><DialogTitle>Catat Pembayaran</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Tanggal</Label><Input type="date" className="mt-1.5" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} /></div>
          <div><Label>Jenis</Label>
            <Select value={f.payment_type} onValueChange={(v) => setF({ ...f, payment_type: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Nominal (Rp)</Label><Input type="number" className="mt-1.5" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payment-amount-input" /></div>
          <div><Label>Metode</Label><Input className="mt-1.5" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} /></div>
          <div><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button className="bg-brand text-white" onClick={submit} data-testid="btn-save-payment">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TaskDialog = ({ open, setOpen, bookingId, divisions, onDone }) => {
  const [f, setF] = useState({ title: "", assigned_staff: "", due_date: new Date().toISOString().slice(0, 10), priority: "Normal", status: "To Do", division_id: "", notes: "" });
  const submit = async () => {
    try {
      await api.post("/tasks", { booking_id: bookingId, ...f, division_id: f.division_id || null });
      toast.success("Tugas dibuat"); setOpen(false); onDone();
      setF({ title: "", assigned_staff: "", due_date: new Date().toISOString().slice(0, 10), priority: "Normal", status: "To Do", division_id: "", notes: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand text-white" data-testid="btn-add-task"><Plus className="w-4 h-4 mr-1.5" /> Tambah Tugas</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Tugas Baru</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Judul</Label><Input className="mt-1.5" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title-input" /></div>
          <div><Label>Divisi</Label>
            <Select value={f.division_id} onValueChange={(v) => setF({ ...f, division_id: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pilih divisi" /></SelectTrigger>
              <SelectContent>{divisions.map((d) => <SelectItem key={d.division_id} value={d.division_id}>{d.division_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Jatuh Tempo</Label><Input type="date" className="mt-1.5" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
            <div><Label>Prioritas</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{TASK_PRIORITY_LABEL[p]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Staff</Label><Input className="mt-1.5" value={f.assigned_staff} onChange={(e) => setF({ ...f, assigned_staff: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button className="bg-brand text-white" onClick={submit} disabled={!f.title} data-testid="btn-save-task">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditBookingDialog = ({ open, setOpen, booking, onDone }) => {
  const [f, setF] = useState({});
  const [reason, setReason] = useState("");
  const [products, setProducts] = useState([]);
  const [divisionIds, setDivisionIds] = useState([]);
  const [productId, setProductId] = useState("");
  const DIVS = require("../lib/constants").DIVISIONS;
  useEffect(() => {
    if (booking) {
      setF({
        event_name: booking.event_name, event_date: booking.event_date, start_time: booking.start_time,
        venue: booking.venue, location: booking.location, guest_count: booking.guest_count,
        package_name: booking.package_name, total_value: booking.total_value, discount: booking.discount,
        assigned_admin: booking.assigned_admin, status: booking.status, notes: booking.notes,
      });
      setDivisionIds((booking.services || []).map((s) => s.division_id));
      setProductId("");
    }
  }, [booking, open]);
  useEffect(() => {
    if (!open) return;
    api.get(`/products?active_only=true`).then(({ data }) => setProducts(data || [])).catch(() => {});
  }, [open]);
  const toggleDiv = (id) => setDivisionIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  // Auto-check divisi ketika user pilih paket dari Master Pricelist
  useEffect(() => {
    if (!productId || productId === "__manual__") return;
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    const incl = Array.isArray(p.included_divisions) ? p.included_divisions.filter(Boolean) : [];
    // Fallback: kalau included_divisions kosong, coba infer dari brand ("MUA Dessy Wedding + Aesthetic Decoration + …")
    const inferred = [];
    if (incl.length === 0) {
      const brand = (p.brand || "").toLowerCase();
      const pname = (p.name || "").toLowerCase();
      const BRAND_MAP = [
        ["mua dessy wedding", "div-mua"], ["dessy wedding", "div-mua"], [" mua", "div-mua"],
        ["aesthetic decoration", "div-decor"], [" decor", "div-decor"], ["dekor", "div-decor"],
        ["project by historia", "div-photo"], [" pbh", "div-photo"], ["photography", "div-photo"], ["videography", "div-photo"],
        ["historia attire", "div-attire"], ["attire", "div-attire"],
        ["historia musical", "div-music"], ["musical", "div-music"],
        ["historia snap", "div-snap"], [" snap", "div-snap"],
        ["historia's studio", "div-studio"], ["historia studio", "div-studio"],
        ["g-ka wedding organizer", "div-wo"], ["wedding organizer", "div-wo"],
        ["historia handcraft", "div-handcraft"], ["handcraft", "div-handcraft"],
        ["wara wiri", "div-warawiri"], ["warawiri", "div-warawiri"],
        ["wedding effect", "div-effect"], ["effect", "div-effect"],
        ["upacara adat", "div-adat"], [" adat", "div-adat"],
      ];
      BRAND_MAP.forEach(([kw, did]) => { if (brand.includes(kw) && !inferred.includes(did)) inferred.push(did); });
      // DWG All-in / brand = "DWG" (legacy) → infer dari product_family atau nama paket
      const isDwgAllIn = brand.includes("dwg all-in") || brand.trim() === "dwg";
      if (inferred.length === 0 && isDwgAllIn) {
        const fam = (p.product_family || "").toUpperCase();
        const nm = pname;
        if (fam.includes("INDOOR") || fam.includes("OUTDOOR") || nm.includes("indoor") || nm.includes("outdoor")) {
          inferred.push("div-mua", "div-decor", "div-photo", "div-wo");
        } else if (fam.includes("ESSENTIAL") || nm.includes("essential")) {
          inferred.push("div-mua", "div-decor", "div-photo");
        } else if (fam.includes("AKAD") || fam.includes("SIRAMAN") || nm.includes("akad") || nm.includes("siraman")) {
          inferred.push("div-mua", "div-decor");
        } else if (fam.includes("PBH") || fam.includes("PROJECT BY HISTORIA") || nm.includes("pbh")) {
          inferred.push("div-photo");
        } else {
          inferred.push("div-mua", "div-decor");
        }
      }
      // Terakhir fallback: divisi tunggal
      if (inferred.length === 0 && p.division_id) inferred.push(p.division_id);
    }
    const nextDivs = incl.length > 0 ? incl : inferred;
    if (nextDivs.length === 0) return;
    setDivisionIds((prev) => Array.from(new Set([...(prev || []), ...nextDivs])));
    // Auto-isi nama paket + total kalau kosong
    setF((prev) => ({
      ...prev,
      package_name: prev.package_name || p.name || "",
      total_value: prev.total_value ? prev.total_value : (p.price || prev.total_value),
    }));
  }, [productId, products]);
  const submit = async () => {
    try {
      const payload = { ...f, total_value: Number(f.total_value), discount: Number(f.discount),
                        guest_count: Number(f.guest_count), change_reason: reason,
                        division_ids: divisionIds };
      if (productId && productId !== "__manual__") payload.product_id = productId;
      await api.put(`/bookings/${booking.id}`, payload);
      toast.success("Pemesanan diperbarui — divisi terkait tersinkron");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dwgPackages = products.filter((p) => p.kind === "package" && !p.division_id);
  const divPackages = products.filter((p) => p.kind === "package" && p.division_id);
  const divProducts = products.filter((p) => p.kind !== "package");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Pemesanan</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2"><Label>Nama Acara</Label><Input className="mt-1.5" value={f.event_name || ""} onChange={(e) => setF({ ...f, event_name: e.target.value })} /></div>
          <div><Label>Tanggal Acara</Label><Input type="date" className="mt-1.5" value={f.event_date || ""} onChange={(e) => setF({ ...f, event_date: e.target.value })} data-testid="edit-event-date" /></div>
          <div><Label>Jam Mulai</Label><Input type="time" className="mt-1.5" value={f.start_time || ""} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
          <div><Label>Venue</Label><Input className="mt-1.5" value={f.venue || ""} onChange={(e) => setF({ ...f, venue: e.target.value })} /></div>
          <div><Label>Kota</Label><Input className="mt-1.5" value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
          <div><Label>Jumlah Tamu</Label><Input type="number" className="mt-1.5" value={f.guest_count || 0} onChange={(e) => setF({ ...f, guest_count: e.target.value })} /></div>
          <div className="col-span-2">
            <Label>Paket dari Master Pricelist</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="mt-1.5" data-testid="edit-booking-package"><SelectValue placeholder={f.package_name ? `Saat ini: ${f.package_name}` : "Pilih paket / satuan"}/></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__manual__">— Tetap manual (isi nama di bawah) —</SelectItem>
                {dwgPackages.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Dessy Wedding Group</div>}
                {dwgPackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
                {divPackages.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Divisi</div>}
                {divPackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
                {divProducts.length > 0 && <div className="text-[10px] uppercase text-[#8C9096] px-2 py-1 mt-1">Paket Satuan</div>}
                {divProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatIDR(p.price)}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-[#8C9096] mt-1">Memilih paket akan otomatis menambahkan divisi yang tercakup ke Layanan Divisi. Tidak menghapus divisi yang sudah aktif.</div>
          </div>
          <div><Label>Nama Paket (override manual)</Label><Input className="mt-1.5" value={f.package_name || ""} onChange={(e) => setF({ ...f, package_name: e.target.value })} /></div>
          <div><Label>Total Kontrak</Label><Input type="number" className="mt-1.5" value={f.total_value || 0} onChange={(e) => setF({ ...f, total_value: e.target.value })} /></div>
          <div><Label>Diskon</Label><Input type="number" className="mt-1.5" value={f.discount || 0} onChange={(e) => setF({ ...f, discount: e.target.value })} /></div>
          <div><Label>Admin PIC</Label><Input className="mt-1.5" value={f.assigned_admin || ""} onChange={(e) => setF({ ...f, assigned_admin: e.target.value })} /></div>
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s}>{BOOKING_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Vendor / Divisi Terlibat</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1.5 p-2 border border-[#EAE3D6] rounded-md bg-[#FAF6EE]">
              {DIVS.map((d) => (
                <label key={d.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white text-sm cursor-pointer" data-testid={`edit-div-check-${d.id}`}>
                  <input type="checkbox" checked={divisionIds.includes(d.id)} onChange={() => toggleDiv(d.id)}/>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${d.color}`}>{d.short}</span>
                  <span className="text-xs">{d.name}</span>
                </label>
              ))}
            </div>
            <div className="text-[11px] text-[#8C9096] mt-1">Uncek divisi hanya menghapus bila belum ada tugas/gaji/progress. Divisi yang sudah aktif tidak akan dihapus.</div>
          </div>
          <div className="col-span-2"><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <div className="col-span-2"><Label>Alasan Perubahan (untuk audit)</Label><Input className="mt-1.5" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="cth: Permintaan klien" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button className="bg-brand text-white" onClick={submit} data-testid="btn-save-edit-booking">Simpan Perubahan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
